import * as THREE from "three";
import { dom } from "../dom";
import {
  ANNOTATION_STORAGE_PREFIX,
  type AnnotationRecord,
  PRIORITY_LABEL_RU,
  state,
} from "../state";
import { annotationLayer } from "../viewer/scene";
import { getExpressIdBoundingCenter as getFragmentsExpressIdCenter } from "../fragments/helpers";
import { closeModal, openModal } from "../ui/modals";
import { setStatus } from "../ui/status";
import { escapeHtmlText, sanitizeAnnotationHtml, stripHtmlToText } from "../utils/html";

function priorityLabelRu(code: string): string {
  return PRIORITY_LABEL_RU[code] || code;
}

function annotationStorageKey(fileKey: string): string {
  return `${ANNOTATION_STORAGE_PREFIX}${fileKey}`;
}

export function persistAnnotations(): void {
  if (!state.currentIfcFileKey) {
    return;
  }
  try {
    localStorage.setItem(
      annotationStorageKey(state.currentIfcFileKey),
      JSON.stringify(state.annotations),
    );
  } catch (_) {
    /* quota or private mode */
  }
}

export function loadAnnotationsForCurrentFile(): void {
  state.annotations.length = 0;
  state.annotationIdCounter = 1;
  if (!state.currentIfcFileKey) {
    return;
  }
  try {
    const raw = localStorage.getItem(annotationStorageKey(state.currentIfcFileKey));
    if (!raw) {
      return;
    }
    const data = JSON.parse(raw) as unknown;
    if (!Array.isArray(data)) {
      return;
    }
    let maxId = 0;
    for (const item of data as any[]) {
      if (!item || typeof item !== "object") {
        continue;
      }
      const id = Number(item.id);
      const elementIds = Array.isArray(item.elementIds)
        ? item.elementIds.map((n: unknown) => Number(n)).filter((n: number) => Number.isFinite(n))
        : [];
      const safeId = Number.isFinite(id) ? id : state.annotationIdCounter++;
      maxId = Math.max(maxId, safeId);
      state.annotations.push({
        id: safeId,
        title: String(item.title || ""),
        priority: typeof item.priority === "string" ? item.priority : "normal",
        deadline: String(item.deadline || ""),
        descriptionHtml: sanitizeAnnotationHtml(String(item.descriptionHtml || "")),
        elementIds,
      });
    }
    state.annotationIdCounter = Math.max(state.annotationIdCounter, maxId + 1);
  } catch (_) {
    /* ignore corrupt storage */
  }
}

export function disposeAnnotationSprites(): void {
  while (annotationLayer.children.length > 0) {
    const o = annotationLayer.children[0] as THREE.Sprite;
    annotationLayer.remove(o);
    const m = o.material as THREE.SpriteMaterial | undefined;
    if (m?.map) {
      m.map.dispose();
    }
    m?.dispose?.();
  }
}

function makeWarningTriangleSprite(): THREE.Sprite | null {
  const canvas2d = document.createElement("canvas");
  canvas2d.width = 128;
  canvas2d.height = 128;
  const ctx = canvas2d.getContext("2d");
  if (!ctx) {
    return null;
  }
  ctx.clearRect(0, 0, 128, 128);
  ctx.fillStyle = "#facc15";
  ctx.strokeStyle = "#854d0e";
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(64, 12);
  ctx.lineTo(116, 108);
  ctx.lineTo(12, 108);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = "#1c1917";
  ctx.font = "bold 56px Arial,sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("!", 64, 74);
  const texture = new THREE.CanvasTexture(canvas2d);
  texture.needsUpdate = true;
  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: false,
    depthWrite: false,
  });
  const sprite = new THREE.Sprite(material);
  sprite.scale.set(0.38, 0.38, 1);
  sprite.renderOrder = 2000;
  return sprite;
}

export function refreshAnnotationMarkers(): void {
  disposeAnnotationSprites();
  if (!state.ifcModel) {
    return;
  }
  void (async () => {
    const ids = new Set<number>();
    for (const ann of state.annotations) {
      for (const eid of ann.elementIds) {
        ids.add(eid);
      }
    }
    for (const expressId of ids) {
      const center = await getFragmentsExpressIdCenter(expressId);
      if (!center) {
        continue;
      }
      addAnnotationSpriteAt(center);
    }
  })();
}

function addAnnotationSpriteAt(center: THREE.Vector3): void {
  const sprite = makeWarningTriangleSprite();
  if (!sprite) {
    return;
  }
  sprite.position.copy(center);
  const boxSize = new THREE.Box3().setFromObject(state.ifcModel!).getSize(new THREE.Vector3());
  const offset = Math.max(0.08, Math.min(0.55, Math.max(boxSize.x, boxSize.y, boxSize.z) * 0.012));
  sprite.position.y += offset;
  annotationLayer.add(sprite);
}

export function updateAnnotationUi(): void {
  dom.addAnnotationBtn.disabled = !state.ifcModel || state.selectedExpressIds.size === 0;
  dom.openAnnotationsListBtn.disabled = !state.ifcModel;
}

export function clearSelectedAnnotationImages(): void {
  dom.annotationDescriptionEl
    .querySelectorAll<HTMLImageElement>("img.is-selected")
    .forEach((img) => img.classList.remove("is-selected"));
  state.activeAnnotationImage = null;
}

export function closeAnnotationImageMenu(): void {
  dom.annotationImageMenu.classList.remove("is-open");
  dom.annotationImageMenu.setAttribute("aria-hidden", "true");
}

export function openAnnotationImageMenu(x: number, y: number): void {
  const menuWidth = 260;
  const menuHeight = 44;
  const safeX = Math.max(8, Math.min(window.innerWidth - menuWidth - 8, x));
  const safeY = Math.max(8, Math.min(window.innerHeight - menuHeight - 8, y));
  dom.annotationImageMenu.style.left = `${safeX}px`;
  dom.annotationImageMenu.style.top = `${safeY}px`;
  dom.annotationImageMenu.classList.add("is-open");
  dom.annotationImageMenu.setAttribute("aria-hidden", "false");
}

export function setActiveImageScale(percent: number): void {
  if (!state.activeAnnotationImage) {
    return;
  }
  const clamped = Math.max(10, Math.min(300, percent));
  state.activeAnnotationImage.style.width = `${clamped}%`;
  state.activeAnnotationImage.style.maxWidth = `${clamped}%`;
  state.activeAnnotationImage.style.height = "auto";
  state.activeAnnotationImage.dataset.scale = String(clamped);
}


function normalizeDescriptionHtmlForWord(descriptionHtml: string): string {
  // Рабочая ширина листа A4 с типовыми полями ≈ 16 см.
  // Word плохо понимает CSS-проценты у <img>: чтобы размер был стабильным,
  // мы выставляем HTML-атрибут width (в px) и CSS-ширину в см.
  const PAGE_USABLE_CM = 16;
  const DPI = 96;

  const wrap = document.createElement("div");
  wrap.innerHTML = sanitizeAnnotationHtml(descriptionHtml || "");
  const imgs = wrap.querySelectorAll<HTMLImageElement>("img");
  imgs.forEach((img) => {
    const cssWidth = img.style.width;
    let widthCm: number;

    if (cssWidth.endsWith("%")) {
      const pct = Math.max(10, Math.min(100, parseFloat(cssWidth)));
      widthCm = (PAGE_USABLE_CM * pct) / 100;
    } else if (cssWidth.endsWith("px")) {
      const px = parseFloat(cssWidth);
      widthCm = Math.min(PAGE_USABLE_CM, (px / DPI) * 2.54);
    } else {
      // Безопасный дефолт — половина ширины листа.
      widthCm = PAGE_USABLE_CM / 2;
    }

    const widthPx = Math.round((widthCm * DPI) / 2.54);
    img.setAttribute("width", String(widthPx));
    img.removeAttribute("height");
    img.style.width = `${widthCm.toFixed(2)}cm`;
    img.style.maxWidth = "100%";
    img.style.height = "auto";
    img.removeAttribute("data-scale");
  });
  return wrap.innerHTML;
}

function focusDescriptionAtEnd(): void {
  dom.annotationDescriptionEl.focus();
  const range = document.createRange();
  range.selectNodeContents(dom.annotationDescriptionEl);
  range.collapse(false);
  const selection = window.getSelection();
  if (!selection) {
    return;
  }
  selection.removeAllRanges();
  selection.addRange(range);
}

export function insertImageFromDataUrl(dataUrl: string): void {
  const img = document.createElement("img");
  img.src = dataUrl;
  img.alt = "Изображение в описании";
  img.style.width = "320px";
  img.style.maxWidth = "100%";
  img.style.height = "auto";
  img.dataset.scale = "100";
  img.draggable = false;

  if (document.activeElement !== dom.annotationDescriptionEl) {
    focusDescriptionAtEnd();
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    dom.annotationDescriptionEl.appendChild(img);
    dom.annotationDescriptionEl.appendChild(document.createElement("br"));
    return;
  }
  const range = selection.getRangeAt(0);
  range.deleteContents();
  range.insertNode(document.createElement("br"));
  range.insertNode(img);
  range.setStartAfter(img);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
}

export function insertImageFileIntoDescription(file: File): void {
  if (!file || !file.type.startsWith("image/")) {
    return;
  }
  const reader = new FileReader();
  reader.onload = () => {
    const url = reader.result;
    if (typeof url !== "string") {
      return;
    }
    insertImageFromDataUrl(url);
  };
  reader.readAsDataURL(file);
}

export function openAddAnnotationForm(): void {
  if (!state.ifcModel || state.selectedExpressIds.size === 0) {
    setStatus("Выделите один или несколько IFC-элементов для примечания.", true);
    return;
  }
  state.editingAnnotationId = null;
  dom.annotationFormTitle.textContent = "Новое примечание";
  dom.annotationTitleInput.value = "";
  dom.annotationPrioritySelect.value = "normal";
  dom.annotationDeadlineInput.value = "";
  dom.annotationDescriptionEl.innerHTML = "";
  closeAnnotationImageMenu();
  state.activeAnnotationImage = null;
  openModal(dom.annotationFormOverlay);
}

export function openEditAnnotationForm(id: number): void {
  const ann = state.annotations.find((a) => a.id === id);
  if (!ann) {
    return;
  }
  state.editingAnnotationId = id;
  dom.annotationFormTitle.textContent = "Редактирование примечания";
  dom.annotationTitleInput.value = ann.title;
  dom.annotationPrioritySelect.value = PRIORITY_LABEL_RU[ann.priority] ? ann.priority : "normal";
  dom.annotationDeadlineInput.value = ann.deadline || "";
  dom.annotationDescriptionEl.innerHTML = sanitizeAnnotationHtml(ann.descriptionHtml || "");
  closeAnnotationImageMenu();
  state.activeAnnotationImage = null;
  openModal(dom.annotationFormOverlay);
}

export function saveAnnotationFromForm(): void {
  const title = dom.annotationTitleInput.value.trim();
  if (!title) {
    setStatus("Укажите название примечания.", true);
    return;
  }
  const priority = dom.annotationPrioritySelect.value || "normal";
  const deadline = dom.annotationDeadlineInput.value || "";
  const descriptionHtml = sanitizeAnnotationHtml(dom.annotationDescriptionEl.innerHTML.trim());
  if (state.editingAnnotationId !== null) {
    const ann = state.annotations.find((a) => a.id === state.editingAnnotationId);
    if (ann) {
      ann.title = title;
      ann.priority = priority;
      ann.deadline = deadline;
      ann.descriptionHtml = descriptionHtml;
    }
  } else {
    if (!state.ifcModel || state.selectedExpressIds.size === 0) {
      setStatus("Нет привязки к элементам — выделите IFC перед сохранением.", true);
      return;
    }
    const elementIds = [...state.selectedExpressIds].sort((a, b) => a - b);
    state.annotations.push({
      id: state.annotationIdCounter++,
      title,
      priority,
      deadline,
      descriptionHtml,
      elementIds,
    });
  }
  persistAnnotations();
  refreshAnnotationMarkers();
  updateAnnotationUi();
  closeModal(dom.annotationFormOverlay);
  closeAnnotationImageMenu();
  setStatus("Примечание сохранено.");
  renderAnnotationList();
}

export function deleteAnnotationById(id: number): void {
  const i = state.annotations.findIndex((a) => a.id === id);
  if (i === -1) {
    return;
  }
  state.annotations.splice(i, 1);
  persistAnnotations();
  refreshAnnotationMarkers();
  updateAnnotationUi();
  renderAnnotationList();
  setStatus("Примечание удалено.");
}

export function renderAnnotationList(): void {
  if (!dom.annotationListRoot) {
    return;
  }
  if (state.annotations.length === 0) {
    dom.annotationListRoot.innerHTML =
      '<p class="muted" style="margin:0;">Пока нет примечаний. Выделите элементы и нажмите «Примечание к выделению».</p>';
    return;
  }
  dom.annotationListRoot.innerHTML = state.annotations
    .slice()
    .sort((a, b) => b.id - a.id)
    .map((ann: AnnotationRecord) => {
      const idsShort =
        ann.elementIds.length > 12
          ? `${ann.elementIds.slice(0, 12).join(", ")} … (+${ann.elementIds.length - 12})`
          : ann.elementIds.join(", ");
      const preview = stripHtmlToText(ann.descriptionHtml) || "—";
      return (
        `<article class="annotation-card" data-annotation-id="${ann.id}">` +
        `<div class="annotation-card-head">${escapeHtmlText(ann.title)}</div>` +
        `<div class="annotation-card-meta">` +
        `Приоритет: ${escapeHtmlText(priorityLabelRu(ann.priority))}<br>` +
        `Срок: ${ann.deadline ? escapeHtmlText(ann.deadline) : "—"}<br>` +
        `Элементы (Express ID): ${escapeHtmlText(idsShort)}` +
        `</div>` +
        `<div class="annotation-card-preview">${escapeHtmlText(preview)}</div>` +
        `<div class="annotation-card-actions">` +
        `<button type="button" class="ann-edit-btn" data-annotation-id="${ann.id}">Изменить</button>` +
        `<button type="button" class="ann-delete-btn" data-annotation-id="${ann.id}">Удалить</button>` +
        `</div></article>`
      );
    })
    .join("");
}

export function exportAnnotationsToWord(): void {
  if (state.annotations.length === 0) {
    setStatus("Нечего экспортировать: список примечаний пуст.", true);
    return;
  }
  const parts: string[] = [
    "<!DOCTYPE html>",
    '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">',
    "<head>",
    '<meta charset="utf-8">',
    "<title>IFC примечания</title>",
    "</head>",
    "<body>",
    "<h1>Примечания к IFC-модели</h1>",
    `<p>Сгенерировано: ${escapeHtmlText(new Date().toLocaleString("ru-RU"))}</p>`,
  ];
  for (const ann of state.annotations) {
    const ids = ann.elementIds.join(", ");
    parts.push(`<h2>${escapeHtmlText(ann.title)}</h2>`);
    parts.push(`<p><strong>Приоритет:</strong> ${escapeHtmlText(priorityLabelRu(ann.priority))}</p>`);
    parts.push(`<p><strong>Срок:</strong> ${ann.deadline ? escapeHtmlText(ann.deadline) : "—"}</p>`);
    parts.push(`<p><strong>Элементы (Express ID):</strong> ${escapeHtmlText(ids)}</p>`);
    const wordDescription = normalizeDescriptionHtmlForWord(ann.descriptionHtml);
    parts.push(`<div><strong>Описание:</strong></div>${wordDescription || "<p>—</p>"}`);
    parts.push("<hr/>");
  }
  parts.push("</body></html>");
  const html = parts.join("");
  const blob = new Blob(["\ufeff", html], { type: "application/msword" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "ifc-primechaniya.doc";
  a.click();
  URL.revokeObjectURL(url);
  setStatus("Файл Word сохранён (HTML-совместимый .doc).");
}
